import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orgCreditsApi } from '@/services/api/org-credits';
import { projectApi } from '@/services/api/projects';
import type {
  BudgetAllocateDto,
  BudgetTreeNode,
  EpisodeCreditTransferDto,
  OrgCreditLedgerParams,
  OrgReportsRange,
  OrgTokenConsumptionParams,
  ProjectBudgetLedgerParams,
  ProducerCreditLedgerParams,
} from '@/types/org';
import { orgMembersKeys } from '@/hooks/api/useOrgMembers';
import { orgProjectBudgetTreeKeys } from '@/hooks/api/useOrgProjectBudgetTree';
import { invalidateFolderTrees } from '@/hooks/api/useFolders';
import { projectKeys } from '@/hooks/api/useProjects';

export const orgManageCreditsKeys = {
  balance: (orgId: string) => ['org', orgId, 'credits', 'balance'] as const,
  allOrgCredits: (orgId: string) => ['org', orgId, 'credits'] as const,
  ledger: (
    orgId: string,
    params: Required<Pick<OrgCreditLedgerParams, 'page' | 'pageSize'>> &
      Pick<OrgCreditLedgerParams, 'eventType'>
  ) => ['org', orgId, 'credits', 'ledger', params] as const,
  tokenConsumption: (
    orgId: string,
    params: Required<Pick<OrgTokenConsumptionParams, 'page' | 'pageSize'>> &
      Pick<
        OrgTokenConsumptionParams,
        | 'projectId'
        | 'operatorUserId'
        | 'trackType'
        | 'featureType'
        | 'range'
        | 'startDate'
        | 'endDate'
        | 'tzOffset'
      >
  ) => ['org', orgId, 'credits', 'token-consumption', params] as const,
  producerLedger: (
    orgId: string,
    params: Required<Pick<ProducerCreditLedgerParams, 'page' | 'pageSize'>> &
      Pick<
        ProducerCreditLedgerParams,
        'eventType' | 'producerUserId' | 'projectId' | 'producerKeyword' | 'projectKeyword' | 'view'
      >
  ) => ['org', orgId, 'credits', 'producer-ledger', params] as const,
  projectBudgetLedger: (
    orgId: string,
    params: Required<Pick<ProjectBudgetLedgerParams, 'page' | 'pageSize'>> &
      Pick<
        ProjectBudgetLedgerParams,
        'eventType' | 'producerUserId' | 'projectId' | 'projectKeyword'
      >
  ) => ['org', orgId, 'credits', 'project-budget-ledger', params] as const,
  reports: (orgId: string) => ['org', orgId, 'reports'] as const,
  reportsSummary: (orgId: string, range: OrgReportsRange) =>
    [...orgManageCreditsKeys.reports(orgId), 'summary', range] as const,
};

function invalidateOrgCreditsAndBudgetTree(queryClient: QueryClient, orgId: string) {
  void queryClient.invalidateQueries({ queryKey: orgManageCreditsKeys.allOrgCredits(orgId) });
  void queryClient.invalidateQueries({ queryKey: ['context-resource-summary'] });
  void queryClient.invalidateQueries({ queryKey: ['projects', 'detail'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['projects', 'list'], exact: false });
  invalidateFolderTrees(queryClient);
  void queryClient.invalidateQueries({ queryKey: orgMembersKeys.byOrg(orgId) });
  void queryClient.invalidateQueries({
    queryKey: [...orgProjectBudgetTreeKeys.all, orgId],
    exact: false,
  });
}

export function useOrgCreditsBalanceQuery(orgId: string | null | undefined) {
  return useQuery({
    queryKey: orgId ? orgManageCreditsKeys.balance(orgId) : orgManageCreditsKeys.balance(''),
    queryFn: () => orgCreditsApi.getBalance(orgId!),
    enabled: !!orgId,
  });
}

export function useOrgCreditLedgerQuery(
  orgId: string | null | undefined,
  params: OrgCreditLedgerParams = {},
  enabled = true
) {
  const normalizedParams = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    eventType: params.eventType || undefined,
  };

  return useQuery({
    queryKey: orgManageCreditsKeys.ledger(orgId ?? '', normalizedParams),
    queryFn: () => orgCreditsApi.listCreditLedger(orgId!, normalizedParams),
    enabled: enabled && !!orgId,
    placeholderData: keepPreviousData,
  });
}

export function useOrgTokenConsumptionsQuery(
  orgId: string | null | undefined,
  params: OrgTokenConsumptionParams = {},
  enabled = true
) {
  const normalizedParams = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    projectId: params.projectId || undefined,
    operatorUserId: params.operatorUserId || undefined,
    trackType: params.trackType,
    featureType: params.featureType,
    range: params.range || undefined,
    startDate: params.startDate || undefined,
    endDate: params.endDate || undefined,
    tzOffset: params.tzOffset,
  };

  return useQuery({
    queryKey: orgManageCreditsKeys.tokenConsumption(orgId ?? '', normalizedParams),
    queryFn: () => orgCreditsApi.listTokenConsumptions(orgId!, normalizedParams),
    enabled: enabled && !!orgId,
    placeholderData: keepPreviousData,
    // 结算中可能是等待确认或待对账，会长期保留暂占；只有运行中的任务需要追进度。
    refetchInterval: (query) =>
      query.state.data?.items.some((item) => item.billingState === 'in_progress') ? 5000 : false,
  });
}

export function useProducerCreditLedgerQuery(
  orgId: string | null | undefined,
  params: ProducerCreditLedgerParams = {},
  enabled = true
) {
  const normalizedParams = {
    view: params.view ?? 'all',
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    eventType: params.eventType || undefined,
    producerUserId: params.producerUserId || undefined,
    projectId: params.projectId || undefined,
    producerKeyword: params.producerKeyword?.trim() || undefined,
    projectKeyword: params.projectKeyword?.trim() || undefined,
  };

  return useQuery({
    queryKey: orgManageCreditsKeys.producerLedger(orgId ?? '', normalizedParams),
    queryFn: () => orgCreditsApi.listProducerCreditLedger(orgId!, normalizedParams),
    enabled: enabled && !!orgId,
    placeholderData: keepPreviousData,
  });
}

export function useProjectBudgetLedgerQuery(
  orgId: string | null | undefined,
  params: ProjectBudgetLedgerParams = {},
  enabled = true
) {
  const normalizedParams = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    eventType: params.eventType || undefined,
    producerUserId: params.producerUserId || undefined,
    projectId: params.projectId || undefined,
    projectKeyword: params.projectKeyword?.trim() || undefined,
  };

  return useQuery({
    queryKey: orgManageCreditsKeys.projectBudgetLedger(orgId ?? '', normalizedParams),
    queryFn: () => orgCreditsApi.listProjectBudgetLedger(orgId!, normalizedParams),
    enabled: enabled && !!orgId,
    placeholderData: keepPreviousData,
  });
}

export function useOrgReportsSummaryQuery(
  orgId: string | null | undefined,
  range: OrgReportsRange,
  enabled = true
) {
  return useQuery({
    queryKey: orgManageCreditsKeys.reportsSummary(orgId ?? '', range),
    queryFn: () => orgCreditsApi.getReportsSummary(orgId!, { range }),
    enabled: enabled && !!orgId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
}

export function useOrgAllocateProjectBudgetMutation(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: BudgetAllocateDto) => orgCreditsApi.allocateProjectBudget(orgId, dto),
    onSuccess: (_result, dto) => {
      invalidateOrgCreditsAndBudgetTree(queryClient, orgId);
      void queryClient.invalidateQueries({
        queryKey: ['projects', 'detail', orgId, dto.projectId],
      });
    },
  });
}

export function useOrgPatchSubprojectCreditsBudgetMutation(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { projectId: string; aiCreditsBudget: number }) =>
      projectApi.patchSubprojectCreditsBudget(
        args.projectId,
        { aiCreditsBudget: args.aiCreditsBudget },
        orgId
      ),
    onSuccess: (_result, args) => {
      invalidateOrgCreditsAndBudgetTree(queryClient, orgId);
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.details() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(orgId, args.projectId) });
      queryClient.invalidateQueries({ queryKey: ['context-resource-summary'] });
      invalidateFolderTrees(queryClient);
    },
  });
}

export function useOrgTransferEpisodeCreditsMutation(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: EpisodeCreditTransferDto) => orgCreditsApi.transferEpisodeCredits(orgId, dto),
    onSuccess: () => invalidateOrgCreditsAndBudgetTree(queryClient, orgId),
  });
}

export function useOrgSetCreditsAlertThresholdMutation(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threshold: number) => orgCreditsApi.setAlertThreshold(orgId, { threshold }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgManageCreditsKeys.balance(orgId) });
      void queryClient.invalidateQueries({ queryKey: orgManageCreditsKeys.reports(orgId) });
      void queryClient.invalidateQueries({ queryKey: ['context-resource-summary'] });
    },
  });
}

export function useOrgRecallProjectBudgetByProjectMutation(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: BudgetTreeNode | { projectId: string; recallAmount?: number }) =>
      orgCreditsApi.recallProjectBudgetByProject(orgId, {
        projectId: args.projectId,
        recallAmount: 'recallAmount' in args ? args.recallAmount : undefined,
      }),
    onSuccess: () => invalidateOrgCreditsAndBudgetTree(queryClient, orgId),
  });
}
